import { Component, inject, DoCheck } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InputModalService } from 'src/utils/input-modal.service';
import { ButtonsComponent } from '../buttons/buttons.component';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-input-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsComponent],
  templateUrl: './input-modal.component.html',
})
export class InputModalComponent implements DoCheck {
  private inputModalService = inject(InputModalService);

  private readonly VALID_NAME_REGEX = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ _\-()\/\.,%#:\$&*]+$/;

  public localValue = '';
  public mensajeErrorNombre: string | null = null;
  public isComposing = false;

  private pendingValidateSub?: Subscription;
  private prevVisible = false;

  get visible(): boolean {
    return this.inputModalService.modalVisible();
  }

  get title(): string {
    return this.inputModalService.title();
  }

  get message(): string {
    return this.inputModalService.message();
  }

  get inputLabel(): string {
    return this.inputModalService.inputLabel();
  }

  get inputPlaceholder(): string {
    return this.inputModalService.inputPlaceholder();
  }

  get iconClass(): string {
    return this.inputModalService.iconClass();
  }

  get cancelText(): string {
    return this.inputModalService.cancelButtonText();
  }

  get continueText(): string {
    return this.inputModalService.continueButtonText();
  }

  ngDoCheck(): void {
    if (this.visible && !this.prevVisible) {
      try {
        const maybeFn = (this.inputModalService as any).inputValue;
        if (typeof maybeFn === 'function') {
          this.localValue = maybeFn.call(this.inputModalService) || '';
        } else if (typeof maybeFn === 'string') {
          this.localValue = maybeFn || '';
        } else if (maybeFn && typeof maybeFn.value !== 'undefined') {
          this.localValue = String((maybeFn as any).value || '');
        } else {
          this.localValue = '';
        }
      } catch {
        this.localValue = '';
      }
      this.mensajeErrorNombre = null;
    }

    if (!this.visible && this.prevVisible) {
      this.localValue = '';
      this.mensajeErrorNombre = null;
      if (this.pendingValidateSub) {
        this.pendingValidateSub.unsubscribe();
        this.pendingValidateSub = undefined;
      }
    }

    this.prevVisible = this.visible;
  }

  onCompositionStart() {
    this.isComposing = true;
  }

  onCompositionEnd(ev: CompositionEvent) {
    this.isComposing = false;
    this.onInputChange(this.localValue);
  }

  onInputChange(value: string) {
    this.localValue = value;
    if (this.isComposing) {
      return;
    }
    this.validateLocalValue();
  }

  private normalize(v: string) {
    return (v ?? '').trim();
  }

  private validateLocalValue() {
    const v = this.normalize(this.localValue);

    if (!v) {
      this.mensajeErrorNombre = null;
      return;
    }

    if (v.length < 3) {
      this.mensajeErrorNombre = 'El nombre debe tener mínimo 3 caracteres.';
      return;
    }

    if (v.length > 50) {
      this.mensajeErrorNombre = 'El nombre no puede superar los 50 caracteres.';
      return;
    }

    if (!this.VALID_NAME_REGEX.test(v)) {
      this.mensajeErrorNombre = 'El nombre contiene caracteres no permitidos.';
      return;
    }

    try {
      const svc: any = this.inputModalService as any;

      if (typeof svc.existingItems === 'function') {
        const items: string[] = svc.existingItems() || [];
        const exists = items.some(it => String(it || '').trim().toLowerCase() === v.toLowerCase());
        if (exists) {
          this.mensajeErrorNombre = 'Este campo ya existe en la categoría.';
          return;
        } else {
          this.mensajeErrorNombre = null;
          return;
        }
      }

      if (typeof svc.validateName === 'function') {
        if (this.pendingValidateSub) {
          this.pendingValidateSub.unsubscribe();
          this.pendingValidateSub = undefined;
        }
        const result = svc.validateName(v);

        if (result && typeof result.subscribe === 'function') {
          this.pendingValidateSub = result.subscribe((resp: any) => {
            const existe = resp && (resp.existe === true || resp.exists === true || resp.available === false);
            if (existe) {
              this.mensajeErrorNombre = 'Este campo ya existe en la categoría.';
            } else {
              this.mensajeErrorNombre = null;
            }
          }, () => {
            this.mensajeErrorNombre = null;
          });
          return;
        }

        if (result && typeof result.then === 'function') {
          result.then((resp: any) => {
            const existe = resp && (resp.existe === true || resp.exists === true || resp.available === false);
            if (existe) {
              this.mensajeErrorNombre = 'Este campo ya existe en la categoría.';
            } else {
              this.mensajeErrorNombre = null;
            }
          }).catch(() => {
            this.mensajeErrorNombre = null;
          });
          return;
        }
      }

      this.mensajeErrorNombre = null;
      return;
    } catch (err) {
      console.warn('[InputModal] validateLocalValue error', err);
      this.mensajeErrorNombre = null;
    }
  }

  get canContinue(): boolean {
    const v = this.normalize(this.localValue);
    return !!v && !this.mensajeErrorNombre;
  }

  close(): void {
    try {
      if (typeof (this.inputModalService as any).cancel === 'function') {
        (this.inputModalService as any).cancel();
        return;
      }
    } catch { }
    if (typeof (this.inputModalService as any).hide === 'function') {
      (this.inputModalService as any).hide();
    }
  }

  confirm(): void {
    this.validateLocalValue();
    if (!this.canContinue) return;
    try {
      const svc: any = this.inputModalService as any;

      if (svc.inputValue && typeof svc.inputValue.set === 'function') {
        svc.inputValue.set(this.localValue);
      } else if (typeof svc.inputValue === 'function') {
        if (typeof svc.inputValueSet === 'function') {
          svc.inputValueSet(this.localValue);
        } else {
          try { (svc as any).inputValue = this.localValue; } catch {  }
        }
      } else if (typeof svc.inputValueSet === 'function') {
        svc.inputValueSet(this.localValue);
      } else if (typeof svc.setInputValue === 'function') {
        svc.setInputValue(this.localValue);
      } else {
        try { (svc as any).inputValue = this.localValue; } catch {  }
      }
    } catch (err) {
      console.warn('[InputModal] confirm: error setting service value', err);
    }

    try {
      if (typeof (this.inputModalService as any).confirm === 'function') {
        (this.inputModalService as any).confirm();
        return;
      }
    } catch {}
    if (typeof (this.inputModalService as any).hide === 'function') {
      (this.inputModalService as any).hide();
    }
  }
}
